import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaDownload, FaEnvelope, FaLinkedinIn, FaArrowRight, FaCode, FaServer, FaShieldAlt, FaGraduationCap, FaDatabase, FaPython, FaChartBar } from 'react-icons/fa';
import { HiChevronDown } from 'react-icons/hi';
import { SiMoodle } from 'react-icons/si';
import { Button, SectionHeader, Card, Badge, StaggerContainer, StaggerItem } from '../components/common';
import { personalInfo, summary, stats, skills, services, experience, linkedinSkills, companyInfo } from '../data/profileData';

const Home = () => {
  const featuredSkills = skills.primary;
  const featuredExperience = experience.slice(0, 2);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <>
      <Helmet>
        <title>{personalInfo.name} | {personalInfo.headline}</title>
        <meta name="description" content={summary.short} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center hero-pattern overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary-500/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants}>
                <Badge variant="primary" size="lg" className="mb-6">
                  {personalInfo.tagline}
                </Badge>
              </motion.div>

              <motion.h1
                variants={itemVariants}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-dark-800 mb-4 leading-tight whitespace-nowrap"
              >
                Hi, I'm{' '}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-secondary-500">
                  {personalInfo.name}
                </span>
              </motion.h1>

              <motion.h2
                variants={itemVariants}
                className="text-xl md:text-2xl text-dark-600 mb-4 font-medium"
              >
                Educator | Server & Infrastructure Manager | Software Developer | AI Specialist | Compliance Auditor – India
              </motion.h2>

              {/* LinkedIn Headline */}
              <motion.p
                variants={itemVariants}
                className="text-sm md:text-base text-dark-500 mb-6 italic"
              >
                "{personalInfo.headline}"
              </motion.p>

              <motion.p
                variants={itemVariants}
                className="text-lg text-dark-500 mb-8 leading-relaxed max-w-xl"
              >
                {summary.short}
              </motion.p>

              <motion.div
                variants={itemVariants}
                className="mb-8"
              >
                <div className="flex flex-wrap gap-4 mb-2">
                  <Button
                    variant="primary"
                    size="lg"
                    href={personalInfo.resumeLink}
                    icon={<FaDownload />}
                  >
                    Download Resume
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    href="/contact"
                    icon={<FaEnvelope />}
                  >
                    Contact Me
                  </Button>
                </div>
                <p className="text-xs text-dark-400 italic">
                  By accessing this resume, you acknowledge that the information is provided for professional review only.
                </p>
              </motion.div>

              <motion.div
                variants={itemVariants}
                className="flex items-center space-x-4"
              >
                <span className="text-dark-400 text-sm">Connect with me:</span>
                <a
                  href={personalInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center text-primary-500 hover:bg-primary-500 hover:text-white transition-all duration-300"
                  title="LinkedIn Profile"
                >
                  <FaLinkedinIn />
                </a>
              </motion.div>
            </motion.div>

            {/* Right Content - Profile Image */}
            <motion.div
              className="relative hidden lg:block"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <div className="relative">
                {/* Decorative elements */}
                <div className="absolute -top-4 -left-4 w-72 h-72 bg-primary-500/20 rounded-full blur-xl" />
                <div className="absolute -bottom-4 -right-4 w-72 h-72 bg-secondary-500/20 rounded-full blur-xl" />

                {/* Profile Image Container */}
                <div className="relative w-80 h-80 mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-3xl transform rotate-6" />
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-3xl transform rotate-3" />
                  <div className="relative w-full h-full bg-gray-200 rounded-3xl overflow-hidden">
                    <img
                      src="/images/profile-home.jpg"
                      alt="Rakesh Kumar Tripathi"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Floating Stats Card - Experience */}
                <motion.div
                  className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-medium p-4"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <FaCode className="text-primary-500" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-dark-800">25+</div>
                      <div className="text-sm text-dark-500">Years Exp.</div>
                    </div>
                  </div>
                </motion.div>

                {/* Floating Skills Card */}
                <motion.div
                  className="absolute -top-4 -right-8 bg-white rounded-xl shadow-medium p-4"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
                      <FaGraduationCap className="text-secondary-500" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-dark-800">15+</div>
                      <div className="text-sm text-dark-500">Languages</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <HiChevronDown className="w-8 h-8 text-dark-400" />
        </motion.div>
      </section>

      {/* LinkedIn Skills Badges */}
      <section className="py-8 bg-white border-b">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-3">
            {linkedinSkills.map((skill, index) => (
              <motion.span
                key={skill}
                className="px-4 py-2 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-full text-sm font-medium text-dark-700 border border-primary-100"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                {skill}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-r from-primary-500 to-secondary-500">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center text-white"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-white/90 text-sm md:text-base">{stat.label}</div>
                <div className="text-white/70 text-xs mt-1">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="primary" className="mb-4">About Me</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-dark-800 mb-6">
                Seasoned IT Professional & Experienced Trainer
              </h2>
              <p className="text-dark-500 leading-relaxed mb-6">
                I am a seasoned industry professional with over 25+ years of experience in a variety of programming languages and subjects including C, Data Structures, Computer Networks, Operating System, C++, Java, Visual Basic, C#, ASP.Net, PHP, Client Server, Moodle, and many more.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {summary.highlights.slice(0, 6).map((highlight, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <FaArrowRight className="w-4 h-4 text-primary-500 mt-1 flex-shrink-0" />
                    <span className="text-sm text-dark-600">{highlight}</span>
                  </div>
                ))}
              </div>
              <Link to="/about">
                <Button variant="primary" icon={<FaArrowRight />} iconPosition="right">
                  Learn More About Me
                </Button>
              </Link>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {[
                { icon: FaServer, title: 'Linux Expert', desc: 'Server Administration' },
                { icon: FaDatabase, title: 'ERPNext Expert', desc: 'Implementation & Customization' },
                { icon: FaShieldAlt, title: 'Cybersecurity', desc: 'Security Specialist' },
                { icon: FaGraduationCap, title: 'Trainer', desc: '25+ Years Teaching' },
                { icon: FaPython, title: 'Python Expert', desc: 'Development & Automation' },
                { icon: FaChartBar, title: 'Data Analyst', desc: 'Excel & Python Analytics' },
              ].map((item, index) => (
                <Card key={index} elevated className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-primary-100 rounded-xl flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-primary-500" />
                  </div>
                  <h3 className="font-semibold text-dark-800 mb-1">{item.title}</h3>
                  <p className="text-sm text-dark-500">{item.desc}</p>
                </Card>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Primary Skills from LinkedIn */}
      <section className="section-padding gradient-bg-subtle">
        <div className="container-custom">
          <SectionHeader
            badge="LinkedIn Skills"
            title="Core Expertise"
            subtitle="Primary skills as highlighted on my LinkedIn profile"
          />

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {featuredSkills.map((skill) => (
              <StaggerItem key={skill.name}>
                <Card className="text-center" hover>
                  <h4 className="font-semibold text-dark-800 mb-2">{skill.name}</h4>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                    <motion.div
                      className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${skill.level}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.2 }}
                    />
                  </div>
                  <span className="text-xs text-primary-500">{skill.source}</span>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <div className="text-center">
            <Link to="/skills">
              <Button variant="outline" icon={<FaArrowRight />} iconPosition="right">
                View All Skills
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Experience Preview */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader
            badge="Experience"
            title="Professional Journey"
            subtitle="Over two decades of leadership in technology, training, and enterprise solutions"
          />

          <div className="space-y-6 mb-8">
            {featuredExperience.map((exp, index) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card elevated>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-dark-800">{exp.title}</h3>
                      <p className="text-primary-500 font-medium">{exp.company}</p>
                      {exp.companyDescription && (
                        <p className="text-sm text-dark-400 mt-1">{exp.companyDescription}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 md:mt-0">
                      <Badge variant={exp.current ? 'secondary' : 'primary'}>
                        {exp.startDate} - {exp.endDate}
                      </Badge>
                      {exp.duration && (
                        <Badge variant="outline">{exp.duration}</Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-dark-500 mb-4">{exp.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {exp.technologies.slice(0, 6).map((tech) => (
                      <Badge key={tech} variant="outline" size="sm">
                        {tech}
                      </Badge>
                    ))}
                    {exp.technologies.length > 6 && (
                      <Badge variant="dark" size="sm">
                        +{exp.technologies.length - 6} more
                      </Badge>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/experience">
              <Button variant="primary" icon={<FaArrowRight />} iconPosition="right">
                View Full Experience
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <motion.div
            className="bg-white rounded-2xl shadow-medium p-8 md:p-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <Badge variant="secondary" className="mb-4">Current Organization</Badge>
                <h2 className="text-2xl md:text-3xl font-bold text-dark-800 mb-4">
                  {companyInfo.name}
                </h2>
                <p className="text-dark-500 mb-4">{companyInfo.description}</p>
                <p className="text-dark-600 mb-4">
                  Working with <strong>{companyInfo.clients}</strong> across {companyInfo.regions.join(', ')}.
                </p>
                <a
                  href={companyInfo.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-500 hover:text-primary-600 font-medium inline-flex items-center"
                >
                  Visit Turacoz Website <FaArrowRight className="ml-2 w-3 h-3" />
                </a>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {companyInfo.services.slice(0, 4).map((service, index) => (
                  <div
                    key={index}
                    className="p-4 bg-gradient-to-br from-primary-50 to-secondary-50 rounded-lg text-center"
                  >
                    <span className="text-sm font-medium text-dark-700">{service}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-dark-800">
        <div className="container-custom">
          <SectionHeader
            badge="Services"
            title="What I Offer"
            subtitle="Comprehensive IT solutions based on my LinkedIn expertise"
            titleClassName="text-white"
            subtitleClassName="text-gray-400"
          />

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.slice(0, 8).map((service) => (
              <StaggerItem key={service.id}>
                <motion.div
                  className="bg-dark-700 rounded-xl p-6 hover:bg-dark-600 transition-colors duration-300 h-full"
                  whileHover={{ y: -4 }}
                >
                  <div className="w-12 h-12 mb-4 bg-primary-500/20 rounded-lg flex items-center justify-center">
                    <FaCode className="w-6 h-6 text-primary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                  <p className="text-gray-400 text-sm mb-3">{service.description}</p>
                  <span className="text-xs text-primary-400">{service.linkedToSkill}</span>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-500 to-secondary-500">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Let's Work Together
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Looking for an experienced IT professional for training, ERPNext implementation,
              Moodle setup, or technology consulting? Let's connect!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button variant="dark" size="lg" icon={<FaEnvelope />}>
                  Get In Touch
                </Button>
              </Link>
              <Button
                variant="outline"
                size="lg"
                href={personalInfo.linkedin}
                target="_blank"
                className="border-white text-white hover:bg-white hover:text-primary-500"
                icon={<FaLinkedinIn />}
              >
                Connect on LinkedIn
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;
